import { useEffect, useMemo, useState } from 'react';

import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Modal } from '~/components/ui/modal';
import { type EventRegistrationHouseholdMember } from '~/domain/households/queries/get-household-members-for-event-registration.server';

interface Props {
  open: boolean;
  members: EventRegistrationHouseholdMember[];
  authenticatedAthleteId: string | null;
  onClose: () => void;
  onContinue: (selectedMemberIds: string[]) => void;
}

export function HouseholdMemberSelectionModal({
  open,
  members,
  authenticatedAthleteId,
  onClose,
  onContinue,
}: Props) {
  // The authenticated member is selected by default
  const initialSelectedIds = useMemo(() => {
    const initial = new Set<string>();
    const authenticatedMember = members.find((m) => m.athleteId === authenticatedAthleteId);
    if (authenticatedMember) {
      initial.add(authenticatedMember.id);
    }
    return initial;
  }, [members, authenticatedAthleteId]);

  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(initialSelectedIds);

  useEffect(() => {
    if (open) {
      setSelectedMemberIds(new Set(initialSelectedIds));
    }
  }, [open, initialSelectedIds]);

  const handleToggleMember = (memberId: string) => {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  };

  const handleContinue = () => {
    onContinue(Array.from(selectedMemberIds));
  };

  const canContinue = selectedMemberIds.size > 0;

  return (
    <Modal
      title="Select Household Members"
      open={open}
      onClose={onClose}
      maxWidth="md"
      onOpenAutoFocus={(e) => e.preventDefault()}
    >
      <div className="flex flex-col">
        <p className="mb-5 text-body2 text-muted-foreground">
          Would you like to register any other members of your household for this event?
        </p>

        <div className="mb-6 flex flex-col gap-2 px-2">
          <span className="text-body2 font-bold text-sidebar-foreground">My Household</span>
          {members.length > 0 ? (
            members.map((member) => {
              const isChecked = selectedMemberIds.has(member.id);
              const displayName =
                [member.firstName, member.lastName].filter(Boolean).join(' ') || 'Unknown';
              const roleDisplay = member.role ? ` - ${member.role}` : '';

              return (
                <div key={member.id} className="flex items-center gap-3 hover:bg-muted/50">
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => handleToggleMember(member.id)}
                    id={`member-${member.id}`}
                  />
                  <label
                    htmlFor={`member-${member.id}`}
                    className="flex-1 cursor-pointer text-body2"
                  >
                    <span className="font-medium">{displayName}</span>
                    {roleDisplay ? (
                      <span className="text-muted-foreground">{roleDisplay}</span>
                    ) : null}
                  </label>
                </div>
              );
            })
          ) : (
            <p className="text-body2 text-muted-foreground">No household members available.</p>
          )}
        </div>

        <div className="flex justify-center">
          <Button onClick={handleContinue} color="primary" disabled={!canContinue}>
            Continue
          </Button>
        </div>
      </div>
    </Modal>
  );
}
